import Banner from '@/components/UI/Banner';
import { _isNotEmpty, _notNil } from '@/littledash';
import type { CrossDomainFinding, CrossDomainFindingGroup, FindingsSummary, Severity } from '@/model/Agent.model';
import { useMemo, type FC } from 'react';
import SendFindingSource from './SendFindingSource';
import SendOmissionNotice from './SendOmissionNotice';
import { aiNotesLast, IN_VIVO_AI, type SendOmission } from './sendReport';
import styles from './sendResults.module.scss';

interface SendCrossDomainResultsProps {
  groups: Array<CrossDomainFindingGroup>;
  summary: FindingsSummary;
  /** What the cross-domain checks found against what the result shows; null on older results. */
  omission?: SendOmission | null;
  testId?: string;
}

const severityRank: Record<Severity, number> = { error: 0, warning: 1, info: 2 };

const humanize = (value: string): string => value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

/** The label for a domain-set group, e.g. `DM ↔ LB` or `DM ↔ SE ↔ TA`. */
const groupLabel = (domains: Array<string>): string => domains.join(' ↔ ');

/** A React key for a finding within a group; findings carry no id, so combine identifying fields. */
const findingKey = (finding: CrossDomainFinding, index: number): string =>
  `${finding.category}-${finding.key_variable ?? ''}-${index}`;

const bySeverity = (a: CrossDomainFinding, b: CrossDomainFinding): number =>
  severityRank[a.severity] - severityRank[b.severity];

/**
 * Renders the cross-domain tier of a bundle validation: referential-integrity and consistency
 * findings grouped by the sorted set of domains each check spanned (`DM ↔ LB`, `DM ↔ TA ↔ SE`).
 * `omission` drives the headline count and the notice for anything a limit left out.
 */
const SendCrossDomainResults: FC<SendCrossDomainResultsProps> = ({
  groups,
  summary,
  omission,
  testId = 'invivo-send-validator__cross-domain',
}) => {
  const sortedGroups = useMemo(
    () =>
      [...groups].map((group) => ({
        ...group,
        findings: [...group.findings].sort(aiNotesLast(bySeverity)),
      })),
    [groups]
  );

  const blocking = summary.submission_blocking_count;
  // What the checks found, not which groups the result carries: a limit can leave every group out,
  // and a success banner above "Showing 0 of N" would read cleaner than the data.
  const found = omission?.found ?? sortedGroups.length;
  const aiNotes = omission?.aiNotes ?? 0;

  return (
    <div
      className="ui-card pa4"
      data-test-component="SendCrossDomainResults"
      data-test-element="container"
      data-testid={testId}
    >
      <h2 className="f4 fw5 ma0 mb3" data-testid="invivo-send-validator__cross-domain-title">
        Cross-domain checks
      </h2>

      {found > 0 ? (
        <Banner
          critical={blocking > 0}
          warning={blocking === 0}
          dismiss={false}
          testId="invivo-send-validator__cross-domain-banner"
        >
          <p className="ma0 fw5" data-testid="invivo-send-validator__cross-domain-headline">
            {_notNil(omission)
              ? `${found} cross-domain ${found === 1 ? 'finding' : 'findings'}`
              : `${found} cross-domain ${found === 1 ? 'relationship' : 'relationships'} with findings`}
          </p>
        </Banner>
      ) : (
        <Banner success dismiss={false} testId="invivo-send-validator__cross-domain-banner">
          <p className="ma0 fw5" data-testid="invivo-send-validator__cross-domain-headline">
            {aiNotes > 0
              ? `No cross-domain issues found by the validation checks — ${aiNotes} ${IN_VIVO_AI} ${aiNotes === 1 ? 'note' : 'notes'} to review`
              : 'No cross-domain issues found across the bundle'}
          </p>
        </Banner>
      )}

      {_notNil(omission) && (
        <SendOmissionNotice omission={omission} testId="invivo-send-validator__cross-domain-omission" />
      )}

      {sortedGroups.map((group) => (
        <div
          key={groupLabel(group.domains)}
          className="mt3"
          data-test-element="cross-domain-group"
          data-test-key={groupLabel(group.domains)}
          data-testid="invivo-send-validator__cross-domain-group"
        >
          <h3 className="f5 fw5 ma0 mb2" data-test-element="cross-domain-group-label">
            {groupLabel(group.domains)}
          </h3>
          <ul className={styles.issueList}>
            {group.findings.map((finding, index) => (
              <li
                key={findingKey(finding, index)}
                className={styles.issueItem}
                data-test-element="cross-domain-finding"
              >
                <span className={`${styles.severityDot} ${styles[finding.severity]}`} aria-hidden="true" />
                <div className="flex flex-column">
                  <span>
                    <span className={`${styles.severityBadge} ${styles[finding.severity]} mr2`}>
                      {finding.severity}
                    </span>
                    {finding.message}
                  </span>
                  <span className="f6 dark-gray mt1" data-test-element="cross-domain-finding-meta">
                    {humanize(finding.category)}
                    {_notNil(finding.key_variable) ? ` · ${finding.key_variable}` : ''}
                    {_notNil(finding.occurrence_count) ? ` · ${finding.occurrence_count} occurrences` : ''}
                    <SendFindingSource finding={finding} className="ml2" />
                  </span>
                  {_notNil(finding.sample_values) && _isNotEmpty(finding.sample_values) && (
                    <span className="f6 dark-gray mt1" data-test-element="cross-domain-finding-samples">
                      Examples: {finding.sample_values.join(', ')}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

export default SendCrossDomainResults;
